(() => {
  const money = (cents) => new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format((Number(cents) || 0) / 100);

  class SpeshGiftWizard extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.dataset.giftData || '[]');
      this.steps = [...this.querySelectorAll('[data-gift-step]')];
      this.progress = [...this.querySelectorAll('[data-gift-progress]')];
      this.next = this.querySelector('[data-gift-next]');
      this.back = this.querySelector('[data-gift-back]');
      this.submit = this.querySelector('[data-gift-submit]');
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-gift-variant-id]');
      this.planInput = this.querySelector('[data-gift-plan-id]');
      this.price = this.querySelector('[data-gift-price]');
      this.summary = this.querySelector('[data-gift-summary]');
      this.availability = this.querySelector('[data-gift-availability]');
      this.lengthProperty = this.querySelector('[data-gift-length-property]');
      this.frequencyProperty = this.querySelector('[data-gift-frequency-property]');
      this.message = this.querySelector('[data-gift-message]');
      this.messageProperty = this.querySelector('[data-gift-message-property]');
      this.galleryRoot = this.closest('[data-gift-product-section]')?.querySelector('[data-gift-gallery]');
      this.galleryTrack = this.galleryRoot?.querySelector('[data-gift-gallery-track]');
      this.gallerySlides = [...(this.galleryRoot?.querySelectorAll('[data-gift-gallery-slide]') || [])];
      this.galleryThumbs = [...(this.galleryRoot?.querySelectorAll('[data-gift-gallery-thumb]') || [])];
      this.options = [...this.querySelectorAll('[data-gift-option]')];
      this.variants = this.readVariantData();
      this.current = 1;
      this.plan = null;
      this.bind();
      this.update();
    }

    readVariantData() {
      const script = document.querySelector('[data-gift-variant-data]');
      return script ? JSON.parse(script.textContent || '{}').variants || [] : [];
    }

    bind() {
      this.next?.addEventListener('click', () => this.go(this.current + 1));
      this.back?.addEventListener('click', () => this.go(this.current - 1));
      this.querySelectorAll('input[name="gift-length"], input[name="gift-frequency"]').forEach((input) => input.addEventListener('change', () => this.update()));
      this.options.forEach((select) => select.addEventListener('change', () => this.update()));
      this.message?.addEventListener('input', () => this.syncMessage());
      this.syncMessage();
      this.bindGallery();
    }

    bindGallery() {
      if (!this.galleryTrack || this.gallerySlides.length < 2) return;
      const moveTo = (index) => {
        const target = (index + this.gallerySlides.length) % this.gallerySlides.length;
        this.galleryTrack.scrollTo({ left: target * this.galleryTrack.clientWidth, behavior: 'smooth' });
        this.setGalleryActive(target);
      };
      this.galleryRoot.querySelector('[data-gift-gallery-prev]')?.addEventListener('click', () => moveTo(this.activeGalleryIndex - 1));
      this.galleryRoot.querySelector('[data-gift-gallery-next]')?.addEventListener('click', () => moveTo(this.activeGalleryIndex + 1));
      this.galleryThumbs.forEach((thumb) => thumb.addEventListener('click', () => moveTo(Number(thumb.dataset.giftGalleryIndex) || 0)));
      this.galleryTrack.addEventListener('scroll', () => {
        const index = Math.round(this.galleryTrack.scrollLeft / Math.max(this.galleryTrack.clientWidth, 1));
        this.setGalleryActive(index);
      }, { passive: true });
      this.setGalleryActive(0);
    }

    setGalleryActive(index) {
      this.activeGalleryIndex = Math.max(0, Math.min(this.gallerySlides.length - 1, index));
      this.galleryThumbs.forEach((thumb, thumbIndex) => {
        if (thumbIndex === this.activeGalleryIndex) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    selected(name) { return this.querySelector(`input[name="${name}"]:checked`)?.value || ''; }

    go(step) {
      if (step > this.current && !this.valid(this.current)) return;
      this.current = Math.max(1, Math.min(this.steps.length, step));
      this.steps.forEach((item, index) => { item.hidden = index + 1 !== this.current; });
      this.progress.forEach((item, index) => {
        if (index + 1 === this.current) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
      });
      this.back.hidden = this.current === 1;
      this.next.hidden = this.current === this.steps.length;
      this.submit.hidden = this.current !== this.steps.length;
      this.update();
      this.steps[this.current - 1]?.querySelector('input, select, textarea')?.focus();
    }

    valid(step) {
      if (step === 1) return Boolean(this.variant && this.variant.available);
      if (step === 2) return Boolean(this.plan);
      if (step === 3) return Boolean(this.plan && this.variant && this.variant.available && this.allocationFor(this.variant, this.plan));
      return true;
    }

    syncMessage() {
      if (this.messageProperty) this.messageProperty.value = this.message?.value || '';
    }

    findPlan() {
      const length = this.selected('gift-length') || '3';
      const frequency = this.selected('gift-frequency') || 'monthly';
      return this.data.find((plan) => plan.length === length && plan.frequency === frequency) || null;
    }

    findVariant() {
      const values = this.options.map((select) => select.value);
      return this.variants.find((variant) => variant.available && variant.options.every((value, index) => value === values[index])) || this.variants.find((variant) => variant.options.every((value, index) => value === values[index])) || this.variants[0] || null;
    }

    allocationFor(variant, plan) { return variant?.selling_plan_allocations?.find((allocation) => String(allocation.selling_plan_id) === String(plan?.id)); }

    update() {
      this.plan = this.findPlan();
      this.variant = this.findVariant();
      const allocation = this.allocationFor(this.variant, this.plan);
      const displayPrice = allocation?.price ?? this.variant?.price;
      if (this.planInput) this.planInput.value = this.plan?.id || '';
      if (this.variantInput) this.variantInput.value = this.variant?.id || '';
      if (this.price) this.price.textContent = displayPrice ? money(displayPrice) : 'Unavailable';
      if (this.lengthProperty) this.lengthProperty.value = this.selected('gift-length') ? `${this.selected('gift-length')} months` : '';
      if (this.frequencyProperty) this.frequencyProperty.value = this.selected('gift-frequency') || '';
      this.syncMessage();
      if (this.summary) {
        const optionSummary = this.options.map((select) => select.value).filter(Boolean).join(' · ');
        this.summary.textContent = [optionSummary, this.selected('gift-frequency') && this.selected('gift-frequency') === 'biweekly' ? 'Every 2 weeks' : this.selected('gift-frequency'), this.selected('gift-length') && `${this.selected('gift-length')} months`].filter(Boolean).join(' · ') || 'Select your gift options to continue';
      }
      if (this.availability) this.availability.textContent = this.variant?.available ? '' : 'That combination is currently unavailable. Please choose another option.';
      if (this.submit) this.submit.disabled = !this.plan || !allocation || !this.variant?.available;
    }
  }

  if (!customElements.get('spesh-gift-wizard')) customElements.define('spesh-gift-wizard', SpeshGiftWizard);
})();
